import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useSavedBrandTemplates } from "../hooks/useSavedBrandTemplates";
import { useSavedBrandTemplateSwatches } from "../hooks/useSavedBrandTemplateSwatches";
import type { RenameOutcome } from "../hooks/useMyBrandTemplates";
import { brandOfTemplate, type SavedBrandTemplate } from "../services/brandTemplatesApi";
import { MAX_TEMPLATE_NAME_LENGTH, type MyBrandTemplate } from "../services/myBrandTemplatesStorage";
import { clearPendingBrandTemplatePreview, requestBrandTemplatePreview } from "../../asset-library/services/brandTemplatePreviewHandoff";
import styles from "./ApplyBrandPanel.module.css";

/** Rows shown before "Show all remaining templates (N)" — keeps a long org
 *  list from pushing Your Templates (and the apply controls) off-screen. */
const VISIBLE_TEMPLATES_LIMIT = 5;

/** Longest name shown on a card before it is cut off with "…" (BRD EC-06). */
const CARD_NAME_LIMIT = 24;

export const APPLIED_NOTE = "Applied. Changed slides since? Apply again to bring them back in line.";

/** Extraction state of the currently-selected row, as seen from this tab —
 *  "idle" covers both "nothing selected yet" and "a different tab owns the
 *  in-flight/most-recent extraction". */
export type SavedTemplateSelectionStatus = "idle" | "extracting" | "ready" | "error";

/** The note under the selected item (as in the mock): what will be applied, with
 *  the Review & Customise button that opens Customise for it. */
export function SelectedNote({ name, ready, onReview }: Readonly<{ name: string; ready: boolean; onReview: () => void }>) {
  return (
    <div className={styles.selectedNote}>
      <b>{name}</b> will be applied with <b>its colours, fonts and styles</b>
      <div>
        <button type="button" className={styles.reviewBtn} onClick={onReview} disabled={!ready}>
          {ready ? "Review & Customise" : "Extracting…"}
        </button>
      </div>
    </div>
  );
}

/** Shortens a long name for display; the full name goes in the title tooltip. */
export function truncateName(name: string, limit = CARD_NAME_LIMIT): string {
  return name.length > limit ? `${name.slice(0, limit)}…` : name;
}

interface SavedTemplatesTabProps {
  readonly selectedId: string | null;
  readonly disabled: boolean;
  readonly selectedStatus: SavedTemplateSelectionStatus;
  /** Brand template whose Customise changes are set for the next apply (FR-12). */
  readonly customisedId?: string | null;
  /** Id of the source last applied to the whole presentation (FR-08). */
  readonly appliedId?: string | null;
  /** Highlights the Your Templates group for a few seconds (EC-22). */
  readonly isHighlightYours?: boolean;
  readonly onSelect: (template: SavedBrandTemplate) => void;
  /** The selected brand template is no longer in the library after it changed (EC-05). */
  readonly onSelectionUnavailable?: () => void;
  readonly onCustomise: (selection: { kind: "org"; template: SavedBrandTemplate } | { kind: "local"; template: MyBrandTemplate }) => void;
  readonly myTemplates: readonly MyBrandTemplate[];
  readonly onSelectMyTemplate: (template: MyBrandTemplate) => void;
  readonly onRenameMyTemplate: (id: string, name: string) => RenameOutcome;
  readonly onDownloadMyTemplate: (template: MyBrandTemplate) => void;
  readonly onDeleteMyTemplate: (id: string) => void;
}

/** Templates tab: org-managed brand templates from blob storage (see
 *  brandTemplatesApi.ts) — alphabetical, with search once there are more
 *  than 5 — followed by "Your Templates", the user's own saved templates
 *  (see myBrandTemplatesStorage.ts). Every card has a More options (⋯) menu;
 *  Customise opens from there.
 *
 *  A brand template's Preview opens the Asset Library's Brand Templates view
 *  for that same template, in its view-only mode (Use This Template / Switch
 *  Template / Keep This Template — BRD FR-18 to FR-21) rather than its normal
 *  Insert into Deck mode; see brandTemplatePreviewHandoff.ts for how the
 *  request (and the selection snapshot the footer compares against) reaches it. */
export function SavedTemplatesTab({
  selectedId, disabled, selectedStatus, customisedId = null, appliedId = null, isHighlightYours = false,
  onSelect, onSelectionUnavailable, onCustomise, myTemplates, onSelectMyTemplate, onRenameMyTemplate, onDownloadMyTemplate,
  onDeleteMyTemplate,
}: Readonly<SavedTemplatesTabProps>) {
  const { loadState, templates, search, setSearch, retry, brandOptions, brandFilter, setBrandFilter } = useSavedBrandTemplates();

  // If the library changes and the selected template is no longer listed, the
  // selection is cleared and any staged preview of it is dropped (EC-05).
  useEffect(() => {
    if (loadState.status !== "ready" || !selectedId || !onSelectionUnavailable) return;
    const known = templates.some((t) => t.id === selectedId) || myTemplates.some((t) => t.id === selectedId);
    if (known) return;
    clearPendingBrandTemplatePreview();
    onSelectionUnavailable();
  }, [loadState.status, templates, myTemplates, selectedId, onSelectionUnavailable]);
  const [showAll, setShowAll] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  // Alphabetical, ignoring case (FR-09); search matches any part of the name (FR-11).
  const sortedTemplates = useMemo(
    () => [...templates].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
    [templates],
  );
  const query = search.trim();
  const filteredTemplates = useMemo(() => {
    const q = query.toLowerCase();
    return sortedTemplates.filter(
      (t) => (!brandFilter || brandOfTemplate(t) === brandFilter) && (!q || t.name.toLowerCase().includes(q)),
    );
  }, [sortedTemplates, query, brandFilter]);
  // 5 or fewer: everything listed, no search. More than 5: search + first 5 (FR-10).
  const searchable = sortedTemplates.length > VISIBLE_TEMPLATES_LIMIT;
  // A new search narrows a different set of matches — collapse back to the top 5.
  useEffect(() => {
    setShowAll(false);
  }, [search, brandFilter]);
  // A pick made elsewhere (the Asset Library's Brand Templates tab routes in
  // with `selectedId` already set — see pendingSavedTemplateSelection.ts) can
  // land past the top 5. Expand so the selected row is actually visible
  // instead of silently hiding it behind "Show all" — this never fires for a
  // selection made IN this list, since a hidden row can't be clicked.
  useEffect(() => {
    if (!selectedId) return;
    const index = sortedTemplates.findIndex((t) => t.id === selectedId);
    if (index >= VISIBLE_TEMPLATES_LIMIT) setShowAll(true);
  }, [selectedId, sortedTemplates]);
  // While a term is entered every match is listed (FR-11).
  const expanded = showAll || query !== "" || Boolean(brandFilter);
  const visibleTemplates = expanded || !searchable ? filteredTemplates : filteredTemplates.slice(0, VISIBLE_TEMPLATES_LIMIT);
  const remaining = filteredTemplates.length - VISIBLE_TEMPLATES_LIMIT;
  const clearSearch = () => {
    setSearch("");
    searchRef.current?.focus();
  };

  const [pendingDelete, setPendingDelete] = useState<MyBrandTemplate | null>(null);
  const yoursRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isHighlightYours) yoursRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  }, [isHighlightYours]);

  return (
    <div className={styles.sourcePane}>
      {loadState.status === "loading" && <div className={styles.savedTemplatesStatus}>Loading brand templates…</div>}

      {loadState.status === "error" && (
        <div className={`${styles.statusBox} ${styles.statusError}`} role="alert">
          <span className={styles.statusLabel}>Couldn&rsquo;t load brand templates.</span>{" "}
          Check your connection and try again.
          <button type="button" className={styles.reviewBtn} onClick={retry}>
            Try Again
          </button>
        </div>
      )}

      {loadState.status === "ready" && (
        <>
          <div className={styles.savedTemplatesHeader}>
            <span className={styles.savedTemplatesTitle}>
              BRAND TEMPLATES <span aria-hidden="true" title="Managed by your organisation">🔒</span>{" "}
              <span className={styles.savedTemplatesManaged}>managed by your organisation</span>
            </span>
            <span className={styles.savedTemplatesCount}>
              {templates.length} template{templates.length === 1 ? "" : "s"}
            </span>
          </div>

          {templates.length === 0 && (
            <output className={styles.statusBox}>
              <span className={styles.statusLabel}>No brand templates yet.</span>{" "}
              When your organisation publishes brand templates, they&rsquo;ll appear here. Ask your brand
              administrator to add them.
            </output>
          )}

          {searchable && (
            <div className={styles.savedTemplatesToolbar}>
              <input
                ref={searchRef}
                type="search"
                className={styles.savedTemplatesSearch}
                placeholder="Search templates"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape" && search) {
                    e.preventDefault();
                    clearSearch();
                  }
                }}
                aria-label="Search templates"
              />
              <select
                className={styles.savedTemplatesBrandSelect}
                value={brandFilter ?? ""}
                onChange={(e) => setBrandFilter(e.target.value || null)}
                aria-label="Filter by brand"
              >
                <option value="">All brands</option>
                {brandOptions.map((brand) => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          )}
          {searchable && query && filteredTemplates.length > 0 && (
            <output className={styles.savedTemplatesStatus}>
              {filteredTemplates.length} template{filteredTemplates.length === 1 ? "" : "s"} match &ldquo;{query}&rdquo;
            </output>
          )}

          {searchable && (query || brandFilter) && filteredTemplates.length === 0 && (
            <output className={styles.noMatchBox}>
              No templates match.{" "}
              <button
                type="button"
                className={styles.czLinkBtn}
                onClick={() => {
                  setBrandFilter(null);
                  clearSearch();
                }}
              >
                Show all brands
              </button>
            </output>
          )}

          {filteredTemplates.length > 0 && (
            <>
              <div role="radiogroup" aria-label="Brand templates">
<ul className={styles.savedTemplatesList}>
                {visibleTemplates.map((template) => (
                  <SavedTemplateRow
                    key={template.id}
                    template={template}
                    selected={template.id === selectedId}
                    status={template.id === selectedId ? selectedStatus : "idle"}
                    disabled={disabled}
                    customised={template.id === customisedId}
                    isApplied={template.id === appliedId}
                    onSelect={() => onSelect(template)}
                    onCustomise={() => onCustomise({ kind: "org", template })}
                    onPreview={() => requestBrandTemplatePreview({
                      id: template.id,
                      selectedId,
                      selectedName: selectedId
                        ? sortedTemplates.find((t) => t.id === selectedId)?.name ?? myTemplates.find((t) => t.id === selectedId)?.name ?? null
                        : null,
                      appliedId,
                    })}
                  />
                ))}
              </ul>
</div>
              {searchable && !expanded && remaining > 0 && (
                <button type="button" className={styles.showAllTemplatesBtn} onClick={() => setShowAll(true)}>
                  Show all remaining templates ({remaining})
                </button>
              )}
            </>
          )}
        </>
      )}

      {/* Your Templates is not filtered by the search and stays available when
          the library can't be loaded or is empty (FR-13). */}
      {myTemplates.length > 0 && (
        <div ref={yoursRef} className={`${styles.myTemplatesSection} ${isHighlightYours ? styles.highlightGroup : ""}`}>
          <div className={styles.savedTemplatesHeader}>
            <span className={styles.savedTemplatesTitle}>
              YOUR TEMPLATES <span className={styles.savedTemplatesManaged}>&ndash; saved from your uploads, only visible to you</span>
            </span>
          </div>
          <div role="radiogroup" aria-label="Your templates">
<ul className={styles.savedTemplatesList}>
            {myTemplates.map((template) => (
              <MyTemplateRow
                key={template.id}
                template={template}
                selected={template.id === selectedId}
                status={template.id === selectedId ? selectedStatus : "idle"}
                disabled={disabled}
                isApplied={template.id === appliedId}
                takenNames={myTemplates.filter((t) => t.id !== template.id).map((t) => t.name.toLowerCase())}
                onSelect={() => onSelectMyTemplate(template)}
                onCustomise={() => onCustomise({ kind: "local", template })}
                onRename={(name) => onRenameMyTemplate(template.id, name)}
                onDownload={() => onDownloadMyTemplate(template)}
                onDelete={() => setPendingDelete(template)}
              />
            ))}
          </ul>
</div>
        </div>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Delete this template?"
          safeLabel="Cancel"
          confirmLabel="Delete"
          isDanger
          onSafe={() => setPendingDelete(null)}
          onConfirm={() => {
            onDeleteMyTemplate(pendingDelete.id);
            setPendingDelete(null);
          }}
        >
          <b>{pendingDelete.name}</b>
          <br />
          It will be removed from Your Templates. This can&rsquo;t be undone.
        </ConfirmDialog>
      )}
    </div>
  );
}

/** Row-preview swatches shared by both the org and Your Templates rows. */
function RowSwatches({ colors }: Readonly<{ colors: readonly string[] }>) {
  if (colors.length === 0) return null;
  return (
    <span className={styles.themeCardSwatches}>
      {colors.map((hex) => (
        <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} title={`#${hex}`} />
      ))}
    </span>
  );
}

/** The error note shown under a selected row whose brand couldn't be loaded. */
function RowErrorNote({ name }: Readonly<{ name: string }>) {
  return (
    <div className={`${styles.statusBox} ${styles.statusError}`} role="alert">
      Couldn&rsquo;t load <b>{name}</b> — pick another template, or try selecting it again.
    </div>
  );
}

function SavedTemplateRow({ template, selected, status, disabled, customised, isApplied, onSelect, onCustomise, onPreview }: Readonly<{
  template: SavedBrandTemplate;
  selected: boolean;
  status: SavedTemplateSelectionStatus;
  disabled: boolean;
  customised: boolean;
  isApplied: boolean;
  onSelect: () => void;
  onCustomise: () => void;
  onPreview: () => void;
}>) {
  const swatches = useSavedBrandTemplateSwatches(template);
  // Shown but not selectable if there's no colour source at all.
  const selectable = template.themeColors !== null || template.sourceFileUrl !== null;

  return (
    <li className={`${styles.savedTemplateRow} ${selected ? styles.savedTemplateRowSelected : ""}`}>
      <div className={styles.savedTemplateRowTop}>
        <label className={styles.savedTemplateRowMain} title={template.name}>
          <input
            type="radio"
            name="saved-brand-template"
            className={styles.savedTemplateRadio}
            checked={selected}
            disabled={disabled || !selectable}
            onChange={onSelect}
          />
          <span className={styles.savedTemplateName}>{truncateName(template.name)}</span>
          <RowSwatches colors={swatches} />
          {!selectable && <span className={styles.previewOnlyBadge}>Preview only</span>}
        </label>
        {customised && <span className={styles.customisedBadge}>Customised</span>}
        <button
          type="button"
          className={styles.savedTemplateEye}
          aria-label={`Preview ${template.name}`}
          title="Preview"
          onClick={onPreview}
        >
          👁
        </button>
      </div>

      {selected && status === "error" && <RowErrorNote name={template.name} />}
      {selected && status !== "error" && (
        <SelectedNote name={template.name} ready={status === "ready"} onReview={onCustomise} />
      )}
      {selected && isApplied && <div className={styles.appliedNote}>{APPLIED_NOTE}</div>}
    </li>
  );
}

/** Splits a file name into the part before its extension and the rest, so
 *  Rename can pre-select just the name (FR-15). */
function baseNameLength(name: string): number {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? dot : name.length;
}

/** A saved (Your Templates) row — same selection treatment as the org rows,
 *  but its swatches come straight off the entry (no download to fall back to)
 *  and its menu offers Customise, Rename, Download and Delete. */
function MyTemplateRow({ template, selected, status, disabled, isApplied, takenNames, onSelect, onCustomise, onRename, onDownload, onDelete }: Readonly<{
  template: MyBrandTemplate;
  selected: boolean;
  status: SavedTemplateSelectionStatus;
  disabled: boolean;
  isApplied: boolean;
  takenNames: readonly string[];
  onSelect: () => void;
  onCustomise: () => void;
  onRename: (name: string) => RenameOutcome;
  onDownload: () => void;
  onDelete: () => void;
}>) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(template.name);
  const [duplicate, setDuplicate] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!renaming) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(0, baseNameLength(input.value));
  }, [renaming]);

  const startRename = () => {
    setDraft(template.name);
    setDuplicate(false);
    setRenaming(true);
  };

  // Enter or clicking away saves; an empty or unchanged name is not saved and
  // simply closes; a name another template already uses stays open (EC-23).
  const commitRename = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === template.name) {
      setRenaming(false);
      return;
    }
    if (takenNames.includes(trimmed.toLowerCase())) {
      setDuplicate(true);
      return;
    }
    const outcome = onRename(trimmed);
    if (outcome === "duplicate") {
      setDuplicate(true);
      return;
    }
    setRenaming(false);
  };

  return (
    <li className={`${styles.savedTemplateRow} ${selected ? styles.savedTemplateRowSelected : ""}`}>
      <div className={styles.savedTemplateRowTop}>
        {renaming ? (
          <input
            ref={inputRef}
            type="text"
            className={`${styles.renameInput} ${duplicate ? styles.renameInputInvalid : ""}`}
            aria-label={`Rename ${template.name}`}
            aria-invalid={duplicate}
            maxLength={MAX_TEMPLATE_NAME_LENGTH}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDuplicate(false);
            }}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setRenaming(false);
              }
            }}
          />
        ) : (
          <label className={styles.savedTemplateRowMain} title={template.name}>
            <input
              type="radio"
              name="saved-brand-template"
              className={styles.savedTemplateRadio}
              checked={selected}
              disabled={disabled}
              onChange={onSelect}
            />
            <span className={styles.savedTemplateName}>{truncateName(template.name)}</span>
            <RowSwatches colors={template.colors} />
          </label>
        )}
        <MoreMenu
          itemName={template.name}
          disabled={disabled}
          items={[
            { key: "customise", label: "Customise", onSelect: onCustomise },
            { key: "rename", label: "Rename", onSelect: startRename },
            { key: "download", label: "Download", onSelect: onDownload },
            { key: "delete", label: "Delete", onSelect: onDelete, isDanger: true },
          ]}
        />
      </div>
      {renaming && duplicate && (
        <div className={styles.fieldError} role="alert">
          A template with this name already exists. Enter a different name.
        </div>
      )}

      {selected && status === "error" && <RowErrorNote name={template.name} />}
      {selected && status !== "error" && !renaming && (
        <SelectedNote name={template.name} ready={status === "ready"} onReview={onCustomise} />
      )}
      {selected && isApplied && <div className={styles.appliedNote}>{APPLIED_NOTE}</div>}
    </li>
  );
}

// ─── Shared menu and dialog ───────────────────────────────────────────────────

export interface MoreMenuItem {
  readonly key: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  readonly isDanger?: boolean;
}

interface MoreMenuProps {
  /** Name of the item the menu belongs to — makes the trigger's accessible name unique. */
  readonly itemName: string;
  readonly items: readonly MoreMenuItem[];
  readonly disabled?: boolean;
}

/** The "More options (⋯)" menu every item carries (BRD FR-07): closes on an
 *  outside click or Esc (returning focus to the trigger), arrow keys move
 *  between entries, and it flips to the left edge so it stays inside the pane. */
export function MoreMenu({ itemName, items, disabled = false }: Readonly<MoreMenuProps>) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) menuRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
  }, [open]);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const buttons = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? [])];
    if (buttons.length === 0) return;
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === "ArrowDown" ? (index + 1) % buttons.length : (index - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  return (
    <div className={styles.moreMenuWrap}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.moreMenuTrigger}
        aria-label={`More options for ${itemName}`}
        title="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          tabIndex={-1}
          aria-label={`Actions for ${itemName}`}
          className={styles.moreMenu}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={`${styles.moreMenuItem} ${item.isDanger ? styles.moreMenuItemDanger : ""}`}
              disabled={item.disabled}
              onClick={() => {
                close(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface ConfirmDialogProps {
  readonly title: string;
  readonly children: React.ReactNode;
  /** The safe action (Cancel / Keep Editing) — focused when the dialog opens. */
  readonly safeLabel: string;
  readonly confirmLabel: string;
  /** Extra buttons between the safe and confirm actions (e.g. Keep Both). */
  readonly extraActions?: ReadonlyArray<{ label: string; onClick: () => void }>;
  readonly isDanger?: boolean;
  readonly onSafe: () => void;
  readonly onConfirm: () => void;
}

/** In-pane confirmation dialog (role alertdialog, aria-modal). Focus moves to
 *  the safe action on open and returns to the trigger on close; Esc, ✕ and a
 *  click outside all take the safe action (BRD section 9). */
export function ConfirmDialog({
  title, children, safeLabel, confirmLabel, extraActions = [], isDanger = false, onSafe, onConfirm,
}: Readonly<ConfirmDialogProps>) {
  const titleId = useId();
  const safeRef = useRef<HTMLButtonElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    safeRef.current?.focus();
    return () => previous?.focus?.();
  }, []);

  // Escape (caught at the card, per the WAI-ARIA dialog pattern) and a click on the
  // backdrop take the safe action. Native listeners keep mouse/keyboard handlers off
  // the non-interactive backdrop and alertdialog elements; the Close button and safe
  // action give full keyboard parity for the mouse-only backdrop dismiss.
  useEffect(() => {
    const backdrop = backdropRef.current;
    const card = cardRef.current;
    if (!backdrop || !card) return;
    const onBackdropMouseDown = (e: MouseEvent) => {
      if (e.target === backdrop) onSafe();
    };
    const onCardKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onSafe();
    };
    backdrop.addEventListener("mousedown", onBackdropMouseDown);
    card.addEventListener("keydown", onCardKeyDown);
    return () => {
      backdrop.removeEventListener("mousedown", onBackdropMouseDown);
      card.removeEventListener("keydown", onCardKeyDown);
    };
  }, [onSafe]);

  return (
    <div ref={backdropRef} className={styles.dialogBackdrop}>
      <div
        ref={cardRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={styles.dialogCard}
      >
        <div className={styles.dialogHeader}>
          <span id={titleId} className={styles.dialogTitle}>{title}</span>
          <button type="button" className={styles.resultDismiss} aria-label="Close" onClick={onSafe}>
            ×
          </button>
        </div>
        <div className={styles.dialogBody}>{children}</div>
        <div className={styles.dialogActions}>
          <button ref={safeRef} type="button" className={styles.dialogSafeBtn} onClick={onSafe}>
            {safeLabel}
          </button>
          {extraActions.map((a) => (
            <button key={a.label} type="button" className={styles.dialogSafeBtn} onClick={a.onClick}>
              {a.label}
            </button>
          ))}
          <button
            type="button"
            className={`${styles.dialogConfirmBtn} ${isDanger ? styles.dialogConfirmDanger : ""}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
